import React, { useState, useEffect } from 'react';
import {
  getZoneVisitStats,
  getRiskLevelColor,
  getRiskLevelLabel,
  formatDwellTime,
} from '../../../services/highRiskZone.service';

/**
 * ZoneExposureSummary - Summary card showing high-risk zone exposure metrics
 */
const ZoneExposureSummary = ({ alienId, onDataLoaded }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!alienId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getZoneVisitStats(alienId, 30);
        setStats(data);
        if (onDataLoaded) {
          onDataLoaded(data);
        }
      } catch (err) {
        console.error('Error fetching zone visit stats:', err);
        setError('Failed to load zone exposure data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [alienId, onDataLoaded]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">High-Risk Zone Exposure</h3>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">High-Risk Zone Exposure</h3>
        <div className="text-center py-4 text-red-500 text-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No data or no visits
  if (!stats || stats.totalVisits === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">High-Risk Zone Exposure</h3>
        <div className="text-center py-6">
          <div className="text-3xl mb-2">&#x2713;</div>
          <p className="text-sm text-gray-600">No high-risk zone visits in the last 30 days</p>
          <div className="mt-3 inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            Low Exposure
          </div>
        </div>
      </div>
    );
  }

  const exposureScore = stats.zoneExposureScore || 0;
  const exposurePercent = (exposureScore * 100).toFixed(0);

  // Determine exposure level
  let exposureLevel = 'Low';
  let exposureColor = 'green';
  if (exposureScore >= 0.7) {
    exposureLevel = 'Critical';
    exposureColor = 'red';
  } else if (exposureScore >= 0.5) {
    exposureLevel = 'High';
    exposureColor = 'orange';
  } else if (exposureScore >= 0.3) {
    exposureLevel = 'Moderate';
    exposureColor = 'yellow';
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">High-Risk Zone Exposure</h3>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium bg-${exposureColor}-100 text-${exposureColor}-700`}
          style={{
            backgroundColor: exposureColor === 'red' ? '#FEE2E2' :
              exposureColor === 'orange' ? '#FFEDD5' :
                exposureColor === 'yellow' ? '#FEF3C7' : '#DCFCE7',
            color: exposureColor === 'red' ? '#B91C1C' :
              exposureColor === 'orange' ? '#C2410C' :
                exposureColor === 'yellow' ? '#A16207' : '#15803D',
          }}
        >
          {exposureLevel} Exposure
        </span>
      </div>

      {/* Exposure Score Gauge */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Exposure Score</span>
          <span className="text-lg font-bold text-gray-800">{exposurePercent}%</span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.max(exposurePercent, 2)}%`,
              background: `linear-gradient(to right, #22C55E, #F59E0B, #EF4444)`,
            }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard
          label="Total Visits"
          value={stats.totalVisits}
          icon="&#x1F4CD;"
        />
        <StatCard
          label="Unique Zones"
          value={stats.uniqueZones}
          icon="&#x1F3AF;"
        />
        <StatCard
          label="Total Dwell Time"
          value={formatDwellTime(stats.totalDwellSeconds)}
          icon="&#x23F1;"
        />
        <StatCard
          label="Max Risk Level"
          value={getRiskLevelLabel(stats.maxRiskLevel)}
          valueColor={getRiskLevelColor(stats.maxRiskLevel)}
          icon="&#x26A0;"
        />
      </div>

      {/* Category Breakdown */}
      {stats.visitsByCategory && Object.keys(stats.visitsByCategory).length > 0 && (
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">
            Visits by Category
          </h4>
          <div className="space-y-1">
            {Object.entries(stats.visitsByCategory).map(([category, count]) => (
              <CategoryBar key={category} category={category} count={count} total={stats.totalVisits} />
            ))}
          </div>
        </div>
      )}

      {/* Last Visit */}
      {stats.lastVisitTime && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
          Last visit: {new Date(stats.lastVisitTime).toLocaleDateString()} at{' '}
          {new Date(stats.lastVisitTime).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

/**
 * Small stat card component
 */
const StatCard = ({ label, value, icon, valueColor }) => (
  <div className="bg-gray-50 rounded-lg p-2">
    <div className="flex items-center gap-1 mb-1">
      <span className="text-sm">{icon}</span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <div
      className="text-sm font-semibold"
      style={{ color: valueColor || '#374151' }}
    >
      {value}
    </div>
  </div>
);

/**
 * Category breakdown bar
 */
const CategoryBar = ({ category, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const formattedCategory = category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-600 w-32 truncate" title={formattedCategory}>
        {formattedCategory}
      </span>
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-400 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 w-6 text-right">{count}</span>
    </div>
  );
};

export default ZoneExposureSummary;
